import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

import { Button } from "../Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";

interface GenericPaginationProps {
  pages: number;
  hasNext: boolean;
  hasPrev: boolean;
  page: number;
  pageSize: number;
  pageSizeOptions: number[];
  setPageIndex: (pageIndex: number) => void;
  setPageSize: (pageSize: number, page?: number) => void;
  isLoading?: boolean;
}

export const GenericTablePagination = ({
  pages,
  hasNext,
  page,
  pageSize,
  pageSizeOptions,
  setPageIndex,
  setPageSize,
  isLoading,
}: GenericPaginationProps) => {
  if (!page || !pages || !pageSize) {
    return null;
  }

  const safePageSize = pageSize || pageSizeOptions[0];
  const safePage = Math.max(1, Math.min(page, pages));
  const safePages = Math.max(0, pages);

  return (
    <div className="flex items-center justify-between px-2">
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Rows per page</p>

          <Select
            value={safePageSize.toString()}
            onValueChange={value => {
              const newSize = Number(value);
              if (newSize !== safePageSize) {
                setPageSize(newSize, 1);
              }
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={safePageSize.toString()} />
            </SelectTrigger>
            <SelectContent side="top">
              {pageSizeOptions.map(size => (
                <SelectItem key={size} value={`${size}`}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          <span className={isLoading ? "opacity-50" : ""}>
            Page {safePage} {safePages !== 0 && `of ${safePages}`}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          {safePages !== 0 && (
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => setPageIndex(1)}
              disabled={safePage === 1 || isLoading}
            >
              <span className="sr-only fixed">Go to first page</span>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => setPageIndex(safePage - 1)}
            disabled={safePage === 1 || isLoading}
          >
            <span className="sr-only fixed">Go to previous page</span>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              setPageIndex(safePage + 1);
            }}
            disabled={safePage === safePages || !hasNext || isLoading}
          >
            <span className="sr-only fixed">Go to next page</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
          {safePages !== 0 && (
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => setPageIndex(safePages)}
              disabled={safePage === safePages || isLoading}
            >
              <span className="sr-only fixed">Go to last page</span>
              <ChevronsRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
